/**
 * MoneyMojo Application Entry Point
 * 
 * This is the main entry point for the MoneyMojo application.
 * It initializes the React application and renders the App component.
 * 
 * @see .specify/memory/constitution.md
 * @see /Reference/Reference design-review/design-principles.md
 */

// CRITICAL: Import React and ReactDOM first to ensure they're in the main bundle
// This prevents React from being code-split into other chunks
import React from 'react';
import ReactDOM from 'react-dom/client';
// Force React to be used immediately and create a reference to ensure it stays in main bundle
// This prevents Vite from code-splitting React into other chunks
const ReactRef = React;
const ReactDOMRef = ReactDOM;
if (!ReactRef || !ReactDOMRef) {
  throw new Error('React or ReactDOM failed to load');
}
// Export React to ensure it's part of the main bundle's exports
(window as Window & { __REACT__?: typeof ReactRef }).__REACT__ = ReactRef;

import App from './App';
import './index.css';
import { initializeTestAuth } from './utils/test-auth';

// Get the root element
const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Root element not found');
}

// Initialize authentication before rendering the app
async function initApp() {
  // Check if Supabase is configured
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  // Only initialize test auth in development mode
  if (import.meta.env.DEV && supabaseUrl && supabaseAnonKey) {
    await initializeTestAuth();
  }

  // Create React root and render the app
  if (!rootElement) {
    throw new Error('Root element not found');
  }
  const root = ReactDOM.createRoot(rootElement);

  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
}

// Initialize and render the app
initApp();
